import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Async thunks for enum management
export const fetchSystemEnums = createAsyncThunk(
  'recordEnums/fetchSystemEnums',
  async (_, { rejectWithValue }) => {
    try {
      const { recordApi } = await import('app/shared-components/client-records/utils/recordApi');
      const response = await recordApi.getSystemEnums();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUserEnums = createAsyncThunk(
  'recordEnums/fetchUserEnums',
  async (ownerKey, { rejectWithValue }) => {
    try {
      const { recordApi } = await import('app/shared-components/client-records/utils/recordApi');
      const response = await recordApi.getUserEnums(ownerKey);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchEnum = createAsyncThunk(
  'recordEnums/fetchEnum',
  async (enumId) => {
    // TODO: Implement enum API call when needed
    throw new Error('Enum API not implemented yet');
  }
);

export const createEnum = createAsyncThunk(
  'recordEnums/createEnum',
  async (enumData, { rejectWithValue }) => {
    try {
      const { recordApi } = await import('app/shared-components/client-records/utils/recordApi');
      const response = await recordApi.createEnum(enumData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateEnum = createAsyncThunk(
  'recordEnums/updateEnum',
  async ({ enumId, enumData }, { rejectWithValue }) => {
    try {
      // Remove server-managed fields from update payload (keep id)
      const { createdAt, lastModified, version, ...cleanData } = enumData;
      
      // Remove null values from top level and enumValues
      const removeNulls = (obj) => {
        const cleaned = {};
        Object.keys(obj).forEach(key => {
          if (obj[key] !== null && obj[key] !== undefined) {
            cleaned[key] = obj[key];
          }
        });
        return cleaned;
      };
      
      const finalData = removeNulls(cleanData);
      if (finalData.enumValues) {
        finalData.enumValues = finalData.enumValues.map(removeNulls);
      }
      
      const { recordApi } = await import('app/shared-components/client-records/utils/recordApi');
      const response = await recordApi.updateEnum(enumId, finalData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteEnum = createAsyncThunk(
  'recordEnums/deleteEnum',
  async (enumId, { rejectWithValue }) => {
    try {
      const { recordApi } = await import('app/shared-components/client-records/utils/recordApi');
      await recordApi.deleteEnum(enumId);
      return enumId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  enums: {}, // Store enums by ID: { "enum-id": { id, name, values: [...] } }
  loading: false,
  error: null
};

const recordEnumsSlice = createSlice({
  name: 'recordEnums',
  initialState,
  reducers: {
    // Store enums from schema responses
    storeEnumsFromSchema: (state, action) => {
      const { enums } = action.payload;
      if (enums) {
        Object.entries(enums).forEach(([enumId, enumData]) => {
          state.enums[enumId] = enumData;
        });
      }
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch system enums
      .addCase(fetchSystemEnums.fulfilled, (state, action) => {

        if (Array.isArray(action.payload)) {
          // If payload is array of enum objects
          action.payload.forEach(enumObj => {
            if (enumObj.id) {
              state.enums[enumObj.id] = enumObj;
            }
          });
        } else if (action.payload && typeof action.payload === 'object') {
          // If payload is object with enum IDs as keys
          Object.entries(action.payload).forEach(([enumId, enumData]) => {
            state.enums[enumId] = enumData;
          });
        }
      })
      
      // Fetch user enums
      .addCase(fetchUserEnums.fulfilled, (state, action) => {

        if (Array.isArray(action.payload)) {
          // If payload is array of enum objects
          action.payload.forEach(enumObj => {
            if (enumObj.id) {
              state.enums[enumObj.id] = enumObj;
            }
          });
        } else if (action.payload && typeof action.payload === 'object') {
          // If payload is object with enum IDs as keys
          Object.entries(action.payload).forEach(([enumId, enumData]) => {
            state.enums[enumId] = enumData;
          });
        }
      })
      
      // Fetch enum
      .addCase(fetchEnum.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEnum.fulfilled, (state, action) => {
        state.loading = false;
        state.enums[action.payload.id] = action.payload;
      })
      .addCase(fetchEnum.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Create enum
      .addCase(createEnum.fulfilled, (state, action) => {
        state.enums[action.payload.id] = action.payload;
      })
      
      // Update enum
      .addCase(updateEnum.fulfilled, (state, action) => {
        state.enums[action.payload.id] = action.payload;
      })
      
      // Delete enum
      .addCase(deleteEnum.fulfilled, (state, action) => {
        delete state.enums[action.payload];
      });
  }
});

export const { storeEnumsFromSchema, clearError } = recordEnumsSlice.actions;

// Selectors
export const selectAllEnums = (state) => state.recordEnums.enums;
export const selectEnumById = (enumId) => (state) => state.recordEnums.enums[enumId];
export const selectEnumsLoading = (state) => state.recordEnums.loading;
export const selectEnumsError = (state) => state.recordEnums.error;

export default recordEnumsSlice.reducer;